// Root error boundary: catches render-time exceptions anywhere in the tree and
// shows a styled fallback (with a reload action) instead of a blank page.
import { Component } from 'react';
import { Box, Typography, Button, Stack } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { colors } from '../constants';
import { Sentry } from '../sentry';

export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('Render error caught by ErrorBoundary:', error, info);
    // Report to Sentry (no-op when no DSN is configured).
    Sentry.captureException(error, {
      contexts: { react: { componentStack: info?.componentStack } },
    });
  }

  handleReload = () => {
    window.location.assign('/');
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: colors.bgDefault,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
        }}
      >
        <Box
          sx={{
            maxWidth: 440,
            width: '100%',
            textAlign: 'center',
            bgcolor: colors.bgPaper,
            border: `1px solid ${colors.border}`,
            borderRadius: 3,
            p: { xs: 3, sm: 4 },
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 800, color: colors.textPrimary, mb: 1 }}>
            Une erreur est survenue
          </Typography>
          <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 3 }}>
            Quelque chose s'est mal passé lors de l'affichage de la page. Vous pouvez
            recharger pour reprendre là où vous étiez.
          </Typography>
          <Stack direction="row" spacing={1.5} justifyContent="center">
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={this.handleReload}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: 2,
                px: 3,
                background: colors.gradientPrimary,
                '&:hover': { background: colors.gradientPrimaryHover },
              }}
            >
              Recharger
            </Button>
          </Stack>
        </Box>
      </Box>
    );
  }
}
